import { useParams, useNavigate } from "react-router-dom";
import DriverInfoSection from "../Components/DriverInfoSection";
import PassengersInfoSection from "../Components/PassengersInfoSection";
import TripInfoLeftSection from "../Components/TripInfoLeftSection";
import TripRoute from "../Components/TripRoute";
import useTrips from "../hooks/useTrips";
import { Box, Typography } from "@mui/material";
import Loading from "../Components/Loading";
import ErrorHandler from "../Components/ErrorHandler";

const TripInfo = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();

  const {
    tripDetails,
    tripDetailsError,
    tripDetailsIsError,
    tripDetailsLoading,
    driverTrail,
    driverLocation,
    isLiveTrip,
    isSignalRConnected,
    tripStatus,
  } = useTrips({ tripId: tripId });

  // Show loading if tripId is not available
  if (!tripId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading trip ID...</p>
        </div>
      </div>
    );
  }

  // Loading state with skeletons
  if (tripDetailsLoading) {
    return (
      <div className="flex lg:flex-row flex-col justify-between gap-5">
        {/* Left section skeleton */}
        <Box className="lg:w-[40%] w-full">
          <Loading />
        </Box>

        {/* Right section skeleton */}
        <div className="lg:w-[60%] w-full flex flex-col gap-8">
          <Loading />

          <div className="flex md:flex-row flex-col gap-5 justify-between">
            <Box width="48%">
              <Loading />
            </Box>
            <Box width="48%">
              <Loading />
            </Box>
          </div>
        </div>
      </div>
    );
  }

  // Error state with ErrorHandler
  if (tripDetailsIsError) {
    // Custom handling for 401 error (unauthorized)
    if (tripDetailsError?.status === 401) {
      sessionStorage.removeItem("ecoRefreshToken");
      sessionStorage.removeItem("ecoToken");
      navigate("/");
      return null;
    }

    return (
      <ErrorHandler
        statusCode={tripDetailsError?.status}
        message={
          tripDetailsError?.message ||
          "An error occurred while loading trip details"
        }
        onRetry={() => window.location.reload()}
        onGoBack={() => navigate(-1)}
        onGoHome={() => navigate("/")}
        showDetails={process.env.NODE_ENV === "development"}
        errorDetails={tripDetailsError}
      />
    );
  }

  // No data state - use ErrorHandler for 404-like state
  if (!tripDetails?.data) {
    return (
      <ErrorHandler
        statusCode={404}
        message={`Trip with ID "${tripId}" could not be found.`}
        onRetry={() => window.location.reload()}
        onGoBack={() => navigate(-1)}
        onGoHome={() => navigate("/trips")}
        showDetails={false}
      />
    );
  }

  // Successful data load
  const tripData = tripDetails.data;

  return (
    <div className="flex lg:flex-row flex-col justify-between gap-5">
      <TripInfoLeftSection
        tripInfo={tripData?.info}
        id={tripData?.id}
        tripDetailsPassenger={tripData?.trip_passenger_details}
        tripDetailsLuggage={tripData?.trip_luggage_details}
        services={tripData?.services}
      />
      <div className="lg:w-[60%] w-full flex flex-col gap-8">
        <TripRoute
          key={tripId}
          startPoint={tripData.start_point}
          endPoint={tripData.end_point}
          tripData={tripData}
          path={tripData?.info?.compressed_path}
          driverTrail={driverTrail}
          driverLocation={driverLocation}
          isLiveTrip={isLiveTrip}
          tripStatus={tripStatus}
          isSignalRConnected={isSignalRConnected}
        />
        <div className="flex md:flex-row flex-col gap-5 justify-between">
          <DriverInfoSection
            driver={tripData?.driver}
            vehicle={tripData?.car}
          />
          <PassengersInfoSection passengers={tripData?.requests} />
        </div>
      </div>
    </div>
  );
};

export default TripInfo;
